import React from "react"
import { useTranslation } from "react-i18next"

import * as UserApi from "../../api/UserApi"
import { User, UserData, ErrorResponse } from "models"
import { useForm, useAlert } from "hooks"

import { formStatusEnum, FormElement } from "components/Form"
import { Spinner, Button } from "components/Elements"

type UserPasswordProps = {
	userData: Partial<User & UserData>
	refreshUser: () => Promise<void>
}

const defaultFormFields = {
	old_password: "",
	new_password: "",
	confirm_password: "",
}

export const UserPassword: React.FC<UserPasswordProps> = ({ userData, refreshUser }) => {
	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: defaultFormFields,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	const { t } = useTranslation()
	const { alert } = useAlert()

	// Reset Form
	const resetForm = (event?: React.MouseEvent<HTMLButtonElement>) => {
		if (event) event.preventDefault()
		setFormStatus(formStatusEnum.readonly)
		setShowLocalErrors(false)
		setFormFields(defaultFormFields)
	}

	// Change Password
	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const newUserPassword = {
					account_id: userData.account_id,
					row_version: userData.row_version,
					old_password: formFields.old_password,
					new_password: formFields.new_password,
				}

				const { getError } = await UserApi.updatePassword(newUserPassword)
				if (getError()) throw getError()

				alert("success", t("info_update", { ns: "form" }))
				// refresh and reset
				await refreshUser()
				resetForm()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<div className="c-block px-4 lg:px-8 py-6 md:py-10 mt-8 md:mt-16">
			<h2 className="c-block__title text-2xl -top-6">{t("my_password", { ns: "user" })}</h2>

			<form className="mt-7 md:mt-0">
				{isReadonly && (
					<>
						<p>{t("current_password", { ns: "user" })}</p>
						<p className="font-bold text-s">********</p>

						<Button
							variant="primary"
							type="submit"
							size="small"
							onClick={() => setFormStatus(formStatusEnum.ready)}
							className="px-10 py-1 mt-6"
						>
							{t("global.edit")}
						</Button>
					</>
				)}

				{!isReadonly && (
					<>
						<FormElement
							{...formElementCommonProps}
							value={formFields.old_password}
							name="old_password"
							type="password"
							id="password"
							label={t("current_password", {
								ns: "user",
							})}
							className="my-1"
							required={true}
						/>

						<FormElement
							{...formElementCommonProps}
							value={formFields.new_password}
							name="new_password"
							type="password"
							id="password"
							label={t("new_password", { ns: "user" })}
							className="my-1"
							required={true}
						/>

						<FormElement
							{...formElementCommonProps}
							value={formFields.confirm_password}
							name="confirm_password"
							type="password"
							id="confirmPassword"
							label={t("confirm_password", {
								ns: "user",
							})}
							className="mb-3"
							required={true}
							passwordToConfirm={formFields.new_password}
						/>
					</>
				)}

				{isReady && (
					<div className="flex items-center mt-10 justify-center md:justify-start">
						<Button
							variant="secondary"
							type="submit"
							size="small"
							onClick={handleSubmit}
							className="mx-1 px-10 py-1 lg:mx-3"
						>
							{t("global.validate")}
						</Button>
						<Button
							variant="secondaryBordered"
							type="button"
							size="small"
							onClick={resetForm}
							className="mx-1 px-10 py-1 lg:mx-3"
						>
							{t("global.cancel")}
						</Button>
					</div>
				)}

				{isLoading && (
					<div className="flex justify-center my-8">
						<Spinner color={"var(--primaryColor)"} size={"100px"} />
					</div>
				)}
			</form>
		</div>
	)
}
